import axios from 'axios';
import { baseURL, generalEndpoint } from '../config';

const API_BASE_URL = process.env.REACT_APP_API_URL || baseURL + '/' + generalEndpoint;

/**
 * Get all positions from the database
 * @param {string} token - Authentication token
 * @returns {Promise<Array>} Array of position objects
 */
export const getPositions = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    const startTime = Date.now();

    try {

        const response = await axios.get(
            `${API_BASE_URL}/Positions`,
            config
        );

        const endTime = Date.now();
        const duration = endTime - startTime;


        if (duration > 5000) {
            console.warn(`⚠️ Slow API response: ${duration}ms for /api/Positions`);
        }

        return response.data;
    } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;

        console.error('❌ Error fetching positions:', error);
        console.error('⏱️ Failed after:', duration, 'ms');

        if (error.code === 'ECONNABORTED') {
            throw new Error(`Request timeout after ${duration}ms. The server is taking too long to respond.`);
        }

        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }

        throw error.response?.data || error.message;
    }
};

/**
 * Get positions by account number
 * @param {string} token - Authentication token
 * @param {string} accountNumber - Account number to filter positions
 * @returns {Promise<Array>} Array of position objects for the account
 */
export const getPositionsByAccount = async (token, accountNumber) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    const startTime = Date.now();

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Positions/account/${accountNumber}`,
            config
        );

        const endTime = Date.now();
        const duration = endTime - startTime;

        if (duration > 5000) {
            console.warn(`⚠️ Slow API response: ${duration}ms for /api/Positions/account/${accountNumber}`);
        }

        return response.data;
    } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;

        console.error('❌ Error fetching positions by account:', error);
        console.error('⏱️ Failed after:', duration, 'ms');

        if (error.code === 'ECONNABORTED') {
            throw new Error(`Request timeout after ${duration}ms. The server is taking too long to respond.`);
        }

        if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Error status:', error.response.status);
        }

        throw error.response?.data || error.message;
    }
};

/**
 * Get a specific position by ID
 * @param {string} token - Authentication token
 * @param {string} positionId - Position ID
 * @returns {Promise<Object>} Position object
 */
export const getPositionById = async (token, positionId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {

        const response = await axios.get(
            `${API_BASE_URL}/Positions/${positionId}`,
            config
        );

        return response.data;
    } catch (error) {
        console.error('❌ Error fetching position:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Close a position
 * @param {string} token - Authentication token
 * @param {string} positionId - Position ID to close
 * @returns {Promise<Object>} API response
 */
export const closePosition = async (token, positionId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {

        const response = await axios.post(
            `${API_BASE_URL}/Positions/${positionId}/close`,
            {},
            config
        );

        return response.data;
    } catch (error) {
        console.error('❌ Error closing position:', error);
        throw error.response?.data || error.message;
    }
};
